'use client'

import {
  useMemo,
  type ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import type Pusher from 'pusher-js'

import {
  useAccessToken,
  useDecodedAccessToken,
  sendGTMEvent,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  usePersonalizationEligibility,
  PERSONALIZATION_SESSION_KEY,
} from '@b-stock/fe-core/bstock-next/hooks'
import {
  usePusher,
  NestableAnalyticsProvider,
} from '@b-stock/fe-core/bstock-react'
import type {
  StoreFrontDto,
  StoreFrontOptionsDefaultViewEnum,
} from '@b-stock/listing-api-client'
import type { SavedSearchDto } from '@b-stock/saved-search-api-client'
import type {
  AllListingsFilters,
  AllListingsSortByEnum,
  AllListingsSortOrderEnum,
  SimilarSearchRequestDto,
} from '@b-stock/search-api-client'

import { getFdActiveGroup } from '@/buyer-portal/components/features/listing/hooks/useFdActiveGroup'
import { toFirstDibs } from '@/components/listings/listingCard/BaseListingCard/ListingInfo/useCardFirstDibsWindow'
import { maybeFormatFilters } from '@/components/listings/search/SearchFilters/shared/filterUtils'
import {
  summarizeDealBadgeImpressions,
  toDealBadgeSegmentProps,
} from '@/helpers/deals/dealBadgeSegmentProps'
import { resolveDealBadge } from '@/helpers/deals/resolveDealBadge'
import { FeatureFlags } from '@/helpers/featureFlags'
import { toBidderCountSegmentProps } from '@/helpers/listing/bidderCount'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { toFirstDibsSegmentProps } from '@/helpers/telemetry/firstDibsSegmentProps'
import type { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useDealBadgeFlags } from '@/hooks/useDealBadgeFlags'
import { getBidStatus } from '@/queries/CombinedListingSearchResult'
import listingSearchQuery from '@/queries/listingSearchQuery'
import type {
  SearchPage,
  SearchResult,
  SearchResultsPage,
} from '@/queries/listingSearchQuery'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import {
  recentSearchQuery,
  useCreateRecentSearch,
} from '@/queries/recentSearchQuery'
import similarSearchQuery, {
  SIMILAR_SEARCH_LIMIT,
  type SimilarSearchResult,
} from '@/queries/similarSearchQuery'

import useSavedSearchId from '../savedSearch/useSavedSearchId'
import calculateActiveFilterCount from './calculateActiveFilterCount'
import {
  DEFAULT_THRESHOLD,
  getAppliedThreshold,
  hasCategorySelection,
} from './categoryThreshold'
import { defaultState, getDefaultState, getView } from './defaultState'
import formatFilters from './formatFilters'
import ListingSearchContext from './ListingSearchContext'
import listingSearchReducer from './listingSearchReducer'
import type {
  ListingFiltersState,
  ListingSearchScope,
  ListingSearchState,
} from './types'
import { LISTINGS_SORT_PERSONALIZED, LISTINGS_SORT_RECOMMENDED } from './types'
import { useAcceptedPoliciesSearchSync } from './useAcceptedPoliciesSearchSync'
import { useFlagExposure } from './useFlagExposure'
import { useMyBidsListingIdSync } from './useMyBidsListingIdSync'
import {
  hasNonKeywordFilters,
  listingSearchStateToSearchParams,
  resolveSimilarShelf,
} from './utils'

type ImpressionTrackingProps =
  | {
      /** Do not fire listing_card_impression from the provider (e.g. when a child fires it with carousel_name). */
      disableImpressionTracking: true
      screenName?: ScreenName
    }
  | {
      disableImpressionTracking?: false
      /**
       * Drives the impression's deal-badge properties (GLOB-5164). It can't be
       * inferred from `storefront`: the watchlist page mounts this provider too.
       */
      screenName: ScreenName
    }

const ListingSearchProvider = ({
  children,
  allFilters,
  pageDefaultView,
  pusherCredentials3MP,
  pusherCredentialsEnt,
  initialState = defaultState,
  storefront,
  fetchFilters = true,
  disableImpressionTracking = false,
  screenName,
  rankingSurface,
  scope,
  rememberPageSize = false,
}: {
  initialState?: ListingSearchState
  children: ReactNode
  allFilters?: AllListingsFilters
  pageDefaultView?: StoreFrontOptionsDefaultViewEnum
  pusherCredentials3MP: ConstructorParameters<typeof Pusher> | null
  pusherCredentialsEnt: ConstructorParameters<typeof Pusher> | null
  storefront?: StoreFrontDto
  fetchFilters?: boolean
  rankingSurface?: string
  /** Params sent with every search on this page; see `ListingSearchScope`. */
  scope?: ListingSearchScope
  /** Opt this surface in to the remembered page size (still flag-gated). */
  rememberPageSize?: boolean
} & ImpressionTrackingProps) => {
  const dealBadgeFlags = useDealBadgeFlags()
  const enableCcSurfacing = useFlag(FeatureFlags.ENABLE_CC_SURFACING)
  const enableFirstDibs = useFlag(FeatureFlags.ENABLE_FIRST_DIBS)
  const enableMyBidsFilter = useFlag(
    FeatureFlags.ENABLE_MY_BIDS_LISTINGS_FILTER
  )
  const sortVariant = useFlag(FeatureFlags.PERSONALIZED_SORT_AB)
  const isTreatment = sortVariant === 'treatment'
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })
  const enableEligibleOnlyFilter = useFlag(
    FeatureFlags.ENABLE_ELIGIBLE_ONLY_FILTER
  )
  const enableCategoryThreshold = useFlag(
    FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD
  )
  const enableRememberPageSize = useFlag(FeatureFlags.ENABLE_REMEMBER_PAGE_SIZE)
  const isPageSizeRemembered = rememberPageSize && enableRememberPageSize

  // Default view is what you get without query params
  const defaultView = getView({ pageDefaultView, storefront })

  const dynamicDefaultState = useMemo(
    () => getDefaultState({ view: defaultView }),
    [defaultView]
  )

  // Use dynamic default state if no specific initial state provided
  const effectiveInitialState =
    initialState === defaultState ? dynamicDefaultState : initialState

  const [state, dispatch] = useReducer(
    listingSearchReducer(),
    effectiveInitialState
  )
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  const { setSavedSearchId } = useSavedSearchId()
  const { track } = useAnalytics()
  const personalizationEligibility = usePersonalizationEligibility(
    '/home-portal/api/headers'
  )

  const accountId = useDecodedAccessToken()?.accountId ?? null
  // Gated here (not just at the URL/toggle level) so a stale bookmark or a
  // live flag rollback while a buyer has the tab open can't keep fetching
  // and applying the buyer's active-bid id allowlist (see BP-2323).
  const isMyBidsFilterActive = enableMyBidsFilter && !!state.filters.myBids
  const myBidsIdSync = useMyBidsListingIdSync({
    isFilterEnabled: isMyBidsFilterActive,
    accountId,
    currentId: state.id,
    dispatch,
  })

  // Accepting a seller's terms unlocks pricing the auctions service was
  // withholding, so the cached search has to be refetched to pick it up.
  useAcceptedPoliciesSearchSync()

  // Impression tracking state
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [currentImpressionId, setCurrentImpressionId] = useState<string | null>(
    null
  )

  // Recent search storage
  const createRecentSearch = useCreateRecentSearch({
    onSuccess: () => {
      // Invalidate recent searches query to refresh the list
      void queryClient.invalidateQueries({ queryKey: [recentSearchQuery.name] })
    },
  })
  const prevIsFetchingRef = useRef<boolean>(false)
  const lastStoredQueryRef = useRef<string | null>(null)

  // When ENABLE_CC_SURFACING/ENABLE_MY_BIDS_LISTINGS_FILTER/ENABLE_ELIGIBLE_ONLY_FILTER are off, neutralize
  // their filters so a URL-supplied `isCreditCardEnabled=true`/`myBids=true`
  // cannot reach search-svc, inflate the active filter count, or render the
  // "Applied" chip. The controls that set them are already hidden in this
  // case (see AllFilters, MyBidsFilterToggle); this closes the deep-link
  // path. Gated reactively (not at initial state) so a late-resolving LD flag
  // doesn't drop a legitimately-set filter.
  const neutralizeCC = !enableCcSurfacing && state.filters.isCreditCardEnabled
  // myBids also drives `state.id` (the active-bid allowlist fetched by
  // useMyBidsListingIdSync) — null it out here too whenever the flag forces
  // myBids off, so a stale allowlist can't reach search-svc or leak into the
  // URL for the render(s) before useMyBidsListingIdSync's own cleanup effect
  // commits (see BP-2323).
  const neutralizeMyBids = !enableMyBidsFilter && state.filters.myBids
  const neutralizeEligibleOnly =
    !enableEligibleOnlyFilter && state.filters.eligibleOnly
  // Short-circuit to the same `state` reference when nothing needs
  // neutralizing (the common case) so unrelated dispatches (pagination, sort,
  // view) don't churn `effectiveState`'s identity and cascade into consumers
  // memoized on it (e.g. the Pusher subscription effect).
  // Test-B buyers with a category selected but no explicit threshold get the
  // default. Derived here rather than dispatched, so it never resets
  // pagination or saved-search state, and applies whether or not a threshold
  // slider is mounted. The URL keeps only explicit values: it's written from
  // the raw reducer value (`rawCategoryThreshold`), not from effectiveState.
  const applyDefaultThreshold =
    enableCategoryThreshold &&
    state.filters.categoryThreshold === null &&
    hasCategorySelection(state.filters)

  const effectiveState = useMemo(() => {
    if (
      !neutralizeCC &&
      !neutralizeMyBids &&
      !neutralizeEligibleOnly &&
      // Flag off only needs the slow path when a threshold must be stripped.
      (enableCategoryThreshold || state.filters.categoryThreshold === null) &&
      !applyDefaultThreshold
    )
      return state
    return {
      ...state,
      id: neutralizeMyBids ? null : state.id,
      filters: {
        ...state.filters,
        isCreditCardEnabled: neutralizeCC
          ? false
          : state.filters.isCreditCardEnabled,
        myBids: neutralizeMyBids ? false : state.filters.myBids,
        eligibleOnly: neutralizeEligibleOnly
          ? false
          : state.filters.eligibleOnly,
        // Strip threshold for flag-off users so a shared URL doesn't
        // accidentally apply Test-B filtering to Control-A participants.
        categoryThreshold: !enableCategoryThreshold
          ? null
          : applyDefaultThreshold
            ? DEFAULT_THRESHOLD
            : state.filters.categoryThreshold,
      },
    }
  }, [
    state,
    neutralizeCC,
    neutralizeMyBids,
    neutralizeEligibleOnly,
    enableCategoryThreshold,
    applyDefaultThreshold,
  ])

  const params = useMemo(
    () => listingSearchStateToSearchParams(effectiveState, storefront, scope),
    [effectiveState, storefront, scope]
  )

  const fixedParams = useMemo(() => {
    const p = { ...params }
    if (p.sortBy === 'relevance' && !p.q) {
      p.sortBy = dynamicDefaultState.sortBy
      p.sortOrder = dynamicDefaultState.sortOrder
    }
    // Hidden A/B: treatment + eligible users get personalization sort in the
    // API call while the sort dropdown always shows "recommended".
    // Scoped to rankingSurface consumers (ALP/storefront) so other consumers
    // (e.g. homepage carousel) setting sortBy=recommended are not silently
    // swapped and contaminate unrelated experiments.
    if (
      p.sortBy === LISTINGS_SORT_RECOMMENDED[0] &&
      isTreatment &&
      personalizationEligibility === 'eligible' &&
      !!rankingSurface
    ) {
      p.sortBy = LISTINGS_SORT_PERSONALIZED[0]
      p.sortOrder = LISTINGS_SORT_PERSONALIZED[1]
    }
    return p
  }, [
    params,
    dynamicDefaultState,
    isTreatment,
    personalizationEligibility,
    rankingSurface,
  ])

  const { data, isLoading, error, isFetching, isPlaceholderData } =
    listingSearchQuery.useQuery({
      params: fixedParams,
      fetchFilters,
      usePostEndpoint: true,
    })

  // The id-fetch itself finishing isn't the whole story: once it dispatches
  // the new `id` allowlist, this query above still needs to actually refetch
  // with it. Since it uses placeholderData: keepPreviousData, `isLoading`
  // stays false and `data` holds the old (unfiltered) results in that gap.
  // Track the last `state.id` this query has actually settled on (completed
  // a fetch for), so a *later* unrelated refetch (pagination/sort/keyword
  // change) while myBids stays on doesn't re-trigger "still catching up" —
  // only an id change itself should.
  const [idSettledInSearch, setIdSettledInSearch] = useState(state.id)
  useEffect(() => {
    if (!isFetching && idSettledInSearch !== state.id) {
      setIdSettledInSearch(state.id)
    }
  }, [isFetching, state.id, idSettledInSearch])

  const myBidsFilter = useMemo(
    () => ({
      isLoading:
        myBidsIdSync.isLoading ||
        (isMyBidsFilterActive &&
          !myBidsIdSync.isError &&
          idSettledInSearch !== state.id),
      isError: myBidsIdSync.isError,
    }),
    [
      myBidsIdSync.isLoading,
      myBidsIdSync.isError,
      isMyBidsFilterActive,
      idSettledInSearch,
      state.id,
    ]
  )

  // When the search service tells us the personalization profile doesn't exist
  // for this account, flip the session cache to 'false' so the eligibility hook
  // treats the user as ineligible on next evaluation instead of hitting the API.
  const personalizationNotFound = data?.personalizationNotFound
  useEffect(() => {
    // Skip cache-poisoning on storefront pages: personalizationNotFound there
    // means the ranking service doesn't support storefront-scoped queries, not
    // that this user lacks personalization data globally (BP-2579).
    if (personalizationNotFound && !storefront) {
      sessionStorage.setItem(PERSONALIZATION_SESSION_KEY, 'false')
    }
  }, [personalizationNotFound, storefront])

  // Guard: if an ineligible user deep-links to a URL with ?s=personalization
  // (e.g. a shared link or stale bookmark), reset the sort to the page default
  // once eligibility resolves so the dropdown is never left with no matching option.
  // Fires on eligibility resolution only — stays silent while still 'pending'.
  useEffect(() => {
    if (
      state.sortBy === ('personalization' as AllListingsSortByEnum) &&
      personalizationEligibility === 'ineligible'
    ) {
      dispatch({ type: 'RESET_SORT' })
    }
  }, [state.sortBy, personalizationEligibility, dispatch])

  // --- Similar inventory ---
  const ldClient = useLDClient()
  const totalPages = data?.total ? Math.ceil(data.total / data.limit) : 0
  const isLastPage = !isLoading && !!data && state.page >= totalPages
  const {
    eligibleForEndOfList,
    eligibleForZeroResults,
    exposeEndOfList,
    exposeZeroResults,
    reason: similarReason,
  } = useMemo(
    () =>
      resolveSimilarShelf({
        isPlaceholderData,
        total: data?.total,
        isLastPage,
        isGridView: state.view === 'GRID',
        hasFilters: hasNonKeywordFilters(effectiveState.filters, allFilters),
        isMyBidsSurface: !!state.filters.myBids,
        isSearchSurface: !!rankingSurface,
      }),
    [
      isPlaceholderData,
      data?.total,
      isLastPage,
      state.view,
      state.filters.myBids,
      effectiveState.filters,
      allFilters,
      rankingSurface,
    ]
  )

  const endOfListTreatment = useFlagExposure(
    FeatureFlags.ENABLE_SIMILAR_INVENTORY_TO_SEARCH,
    exposeEndOfList,
    ldClient
  )
  const zeroResultsTreatment = useFlagExposure(
    FeatureFlags.ENABLE_PROMOTED_LISTINGS_NO_RESULTS,
    exposeZeroResults,
    ldClient
  )

  const similarEnabled =
    (eligibleForEndOfList && endOfListTreatment) ||
    (eligibleForZeroResults && zeroResultsTreatment)

  const similarSearchDto = useMemo<SimilarSearchRequestDto>(
    () => ({
      originalFilters: {
        ...formatFilters(effectiveState.filters),
        ...(storefront?._id ? { storeFrontId: [storefront._id] } : {}),
      },
      limit: SIMILAR_SEARCH_LIMIT,
      isStorefrontPage: !!storefront,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
    }),
    [effectiveState.filters, state.sortBy, state.sortOrder, storefront]
  )

  const { data: similarData, isFetching: similarIsFetching } =
    similarSearchQuery.useWithOpts([similarSearchDto], {
      enabled: similarEnabled,
    })

  // Log some data about the search that was performed just before presenting
  // results to the user and track impressions
  useEffect(() => {
    if (!isFetching) {
      if (state.filters.q && data) {
        sendGTMEvent({
          event: 'search',
          platform: GTM_PLATFORM,
          search_term: state.filters.q,
          search_results_count: data.total,
        })
      }

      // Clear any existing timeout
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }

      // Set new timeout for impression tracking (skip when child fires with carousel context, e.g. BP-990)
      impressionTimeoutRef.current = setTimeout(() => {
        const impressionId = crypto.randomUUID()
        setCurrentImpressionId(impressionId)

        if (!disableImpressionTracking) {
          // ListingSearchView renders the grid (badges) for any view but LIST
          // (no badges). Known gap: the watchlist page ignores `view`, and its
          // bid-card template draws no badge, so there this over-reports shown
          // badges while ENABLE_FMV_DEAL_BADGE_ALP is on.
          const viewShowsDealBadges = state.view !== 'LIST'
          const cards =
            data?.listings.map((listing) => ({
              id: listing.id,
              recommended_rank: listing.customRank,
              is_control: listing.randomSetLabel,
              ...toDealBadgeSegmentProps(
                resolveDealBadge(listing, screenName, dealBadgeFlags),
                { rendered: viewShowsDealBadges }
              ),
              cc_eligible_tag_shown:
                enableCcSurfacing && listing.isCreditCardEnabled === true,
              is_new_seller:
                isNewSellerTreatment &&
                (newSellerIds?.has(listing.storefrontId) ?? false),
              user_bid_status: getBidStatus(listing),
              ...toBidderCountSegmentProps(listing),
              // Same window rule as the card's First Dibs tag and countdown
              ...(enableFirstDibs &&
                toFirstDibsSegmentProps(
                  getFdActiveGroup(toFirstDibs(listing.firstDibs), Date.now())
                )),
            })) ?? []

          // Include threshold only when non-null AND a category is active;
          // omitted otherwise so Control-A events never carry the key. Read
          // from effectiveState so flag-off users never get one even when a
          // threshold arrives via a shared URL (effectiveState strips it).
          const thresholdForImpression = getAppliedThreshold(
            effectiveState.filters
          )

          track('listing_card_impression', {
            listing_card_impression_id: impressionId,
            listings_count: data?.listings.length,
            total_returned_results: data?.total,
            searchState: effectiveState,
            landed_cost_percent_msrp_top_range:
              state.filters.landedCostPercentage !== null
                ? Math.round(state.filters.landedCostPercentage * 100)
                : null,
            landed_cost_percent_msrp_bottom_range: 0,
            ...summarizeDealBadgeImpressions(cards),
            my_bids_filter: isMyBidsFilterActive,
            ...(thresholdForImpression !== null && {
              threshold: thresholdForImpression,
            }),
            listings: cards,
          })
        }

        if (
          isTreatment &&
          personalizationEligibility === 'eligible' &&
          !!rankingSurface &&
          data &&
          fixedParams.sortBy === LISTINGS_SORT_PERSONALIZED[0]
        ) {
          const listingIds = data.listings.map((l) => l.id)
          track('ranking_served', {
            surface: rankingSurface,
            sort_by: data.personalizationNotFound
              ? LISTINGS_SORT_RECOMMENDED[0]
              : LISTINGS_SORT_PERSONALIZED[0],
            variant: sortVariant,
            listing_ids: listingIds,
            positions: listingIds.map((_, i) => data.offset + i + 1),
            impression_id: impressionId,
          })
        }
      }, 3000)
    }

    // Cleanup function to clear timeout
    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
    // Omission is intentional, some data changes with realtime updates but
    // we don't want to track in that situation
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isFetching, disableImpressionTracking])

  // Store recent search when keyword search starts
  useEffect(() => {
    // Only store when search transitions from not fetching to fetching (search starts)
    const searchJustStarted = isFetching && !prevIsFetchingRef.current
    prevIsFetchingRef.current = isFetching

    if (!searchJustStarted) {
      return
    }

    const searchQuery = state.filters.q
    const trimmedQuery = searchQuery?.trim()

    // Skip if:
    // - No search query or Query is only whitespaces
    // - User is not authenticated
    // - Search came from saved search
    // - Query hasn't changed (same query as last stored)
    if (
      !trimmedQuery ||
      !accessToken ||
      state.appliedFromSavedSearch ||
      lastStoredQueryRef.current === trimmedQuery
    ) {
      return
    }

    // Store the query to prevent duplicate storage
    lastStoredQueryRef.current = trimmedQuery

    createRecentSearch.mutate({
      name: trimmedQuery,
      filters: {
        q: trimmedQuery,
      },
    })
  }, [
    isFetching,
    state.filters.q,
    state.appliedFromSavedSearch,
    accessToken,
    createRecentSearch,
  ])

  // Writes Pusher updates to both the main search cache and the similar inventory cache
  const updateSearchData = useCallback(
    (updater: <T extends SearchPage>(data: T) => T) => {
      queryClient.setQueryData<SearchResultsPage>(
        listingSearchQuery.getKey(accessToken, [
          {
            params,
            fetchFilters,
            usePostEndpoint: true,
          },
        ]),
        (oldData) => {
          if (!oldData) {
            return undefined
          }

          return updater(oldData)
        }
      )
      // Similar Inventory cards reuse ListingCard and call updateSearchData for
      // Pusher updates. Route the same updater here so their bid counts, prices,
      // and time-remaining also stay live.
      queryClient.setQueryData<SimilarSearchResult>(
        similarSearchQuery.getKey(accessToken, [similarSearchDto]),
        (oldData) => {
          if (!oldData) {
            return undefined
          }

          return updater(oldData)
        }
      )
    },
    [params, accessToken, queryClient, fetchFilters, similarSearchDto]
  )

  const [pusherKey3MP, pusherOptions3MP] = pusherCredentials3MP ?? []
  const pusherClient3MP = usePusher(pusherKey3MP, pusherOptions3MP?.cluster)

  // check both main search results and similar inventory results
  const hasEntListings =
    (data?.listings.some((l) => l.auctionEnt) ?? false) ||
    (similarData?.listings.some((l) => l.auctionEnt) ?? false)

  // Freeze Sort kill switch (GLOB-4832): isSortFrozen can only ever be set
  // true via the toggle/banner, which self-gate on this same flag — but if
  // the flag is disabled mid-session (a live LaunchDarkly rollback) while a
  // buyer already has the list frozen, both of those controls unmount,
  // leaving no UI left to unfreeze. Force the reducer's isSortFrozen back to
  // false here instead of merely hiding it, so this goes through the normal
  // dispatch path and the unfreeze effect below (pinnedPages cleanup,
  // freeze_sort_session analytics) runs exactly as it would for a manual
  // unfreeze.
  const enableFreezeSort = useFlag(FeatureFlags.ENABLE_FREEZE_SORT)
  useEffect(() => {
    if (!enableFreezeSort && state.isSortFrozen) {
      dispatch({ type: 'SET_SORT_FROZEN', isSortFrozen: false })
    }
  }, [enableFreezeSort, state.isSortFrozen])

  // Freeze Sort (GLOB-4832): while frozen, snapshot each visited page's
  // listings by id and order, then only ever update existing entries (never
  // add or remove) as fresh data arrives (Pusher pushes, background
  // refetches). This holds row order across all sort modes — not just the
  // endTime case useListingSearchPusherUpdates guards directly — while
  // per-listing field updates (bid, price, countdown, closed/ended status)
  // keep flowing since fields update in place. A listing that drops out of
  // the raw data entirely while frozen (e.g. the server stops returning a
  // closed auction) keeps rendering its last known state instead of
  // disappearing — "Ended"/disabled-bid treatment is already purely
  // time-driven (see EndDateCellRenderer/ActionForm's
  // useIsPast(endTime)), so holding the last snapshot is all BP-2417 needs.
  // New listings appearing while frozen are excluded (BP-2418) since we
  // never add ids to a page's snapshot.
  //
  // Pagination (BP-2420): the search API is paginated server-side — `data`
  // only ever holds the current page's slice, so there's no single flat
  // order to fetch and pin upfront (results can run into the thousands).
  // Instead, each page gets its own pinned snapshot the first time it's
  // visited during this freeze session; navigating back to an
  // already-visited page replays its own pin rather than re-snapshotting.
  // Pages never visited while frozen simply aren't pinned yet.
  //
  // An explicit sort/filter change while frozen (BP-2419) is not a passive
  // Pusher/refetch tick — the user's action wins, so instead of merging into
  // the stale snapshot(s) we drop every pinned page and start fresh from the
  // current page's new results (re-freeze). Detected via a key over just
  // sortBy/sortOrder/filters/id — page/pageSize deliberately excluded so
  // paginating a frozen result set doesn't look like a "change."
  const sortFilterKey = useMemo(
    () =>
      JSON.stringify({
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        filters: state.filters,
        id: state.id,
      }),
    [state.sortBy, state.sortOrder, state.filters, state.id]
  )
  const [pinnedPages, setPinnedPages] = useState<Map<
    number,
    Map<string, SearchResult>
  > | null>(null)
  // Latest-value mirror so the unmount-cleanup effect below (empty deps, so
  // its closure is fixed at mount) can still read the pinned-page count as of
  // whenever the component actually unmounts, not as of first render.
  const pinnedPagesRef = useRef(pinnedPages)
  pinnedPagesRef.current = pinnedPages
  const wasSortFrozenRef = useRef(state.isSortFrozen)
  const pinnedSortFilterKeyRef = useRef(sortFilterKey)

  // freeze_sort_session (GLOB-4832/BP-2422): duration_frozen_secs,
  // bids_placed_while_frozen, and pages_viewed_while_frozen for the current
  // freeze session. `bids_placed_while_frozen` is only ever incremented via
  // recordBidPlacedWhileFrozen (see BidConfirmationModal), which is threaded
  // explicitly through the bid flow rather than read from context directly
  // there — see the comment on recordBidPlacedWhileFrozen in types.ts.
  // A re-freeze triggered by a sort/filter change while already frozen
  // (BP-2419) is a continuation of the same session, not a new one, so it
  // deliberately does not reset freezeStartedAtRef or the bid counter — only
  // the transition out of `isSortFrozen` entirely ends the session.
  const freezeStartedAtRef = useRef<number | null>(null)
  const bidsPlacedWhileFrozenRef = useRef(0)
  const recordBidPlacedWhileFrozen = useCallback(() => {
    bidsPlacedWhileFrozenRef.current += 1
  }, [])

  useEffect(() => {
    if (!wasSortFrozenRef.current && state.isSortFrozen) {
      pinnedSortFilterKeyRef.current = sortFilterKey
      setPinnedPages(new Map())
      freezeStartedAtRef.current = Date.now()
      bidsPlacedWhileFrozenRef.current = 0
    } else if (wasSortFrozenRef.current && !state.isSortFrozen) {
      if (freezeStartedAtRef.current !== null) {
        track('freeze_sort_session', {
          duration_frozen_secs: Math.round(
            (Date.now() - freezeStartedAtRef.current) / 1000
          ),
          bids_placed_while_frozen: bidsPlacedWhileFrozenRef.current,
          pages_viewed_while_frozen: pinnedPages?.size ?? 0,
        })
      }
      freezeStartedAtRef.current = null
      setPinnedPages(null)
      // Dropping the pinned snapshot above only stops re-pinning future
      // updates onto stale rows — it doesn't make `data` itself live again.
      // `staleTime` (5 min) means react-query won't refetch on its own just
      // because the lock cleared, so unfreeze would otherwise keep rendering
      // whatever result set was last cached from before/during the freeze
      // (stale order, missing anything published while frozen) until some
      // unrelated trigger (page reload, a sort/filter change) forces a fresh
      // fetch. Invalidate explicitly so unfreeze always re-runs the search.
      void queryClient.invalidateQueries({
        queryKey: [listingSearchQuery.name],
      })
    }
    wasSortFrozenRef.current = state.isSortFrozen
    // pinnedPages is intentionally included so we read its up-to-date size at
    // the moment of the unfreeze transition; while frozen with no
    // freeze/unfreeze transition, both branches above are no-ops.
  }, [state.isSortFrozen, sortFilterKey, pinnedPages, track, queryClient])

  // Also fire freeze_sort_session if the buyer navigates away (unmounts this
  // provider, e.g. leaving the List View page) while still frozen — that's an
  // implicit end to the session just as much as toggling the control off.
  useEffect(() => {
    return () => {
      if (freezeStartedAtRef.current !== null) {
        track('freeze_sort_session', {
          duration_frozen_secs: Math.round(
            (Date.now() - freezeStartedAtRef.current) / 1000
          ),
          bids_placed_while_frozen: bidsPlacedWhileFrozenRef.current,
          pages_viewed_while_frozen: pinnedPagesRef.current?.size ?? 0,
        })
      }
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!state.isSortFrozen || !data) {
      return
    }

    if (pinnedSortFilterKeyRef.current !== sortFilterKey) {
      // Sort or filters changed while frozen. `data` may still be the
      // pre-change results (placeholderData: keepPreviousData) while the
      // resulting refetch is in flight — wait for it to settle so we
      // re-freeze on the new results, not a stale mid-transition snapshot.
      if (isFetching) {
        return
      }
      pinnedSortFilterKeyRef.current = sortFilterKey
      setPinnedPages(
        new Map([
          [
            state.page,
            new Map(
              data.listings.map((listing) => [listing.listingId, listing])
            ),
          ],
        ])
      )
      return
    }

    setPinnedPages((prev) => {
      if (!prev) {
        return prev
      }

      const pinnedPage = prev.get(state.page)
      if (!pinnedPage) {
        // First visit to this page during the current freeze session — wait
        // for its fetch to settle (same reasoning as the sort/filter case)
        // before pinning it.
        if (isFetching) {
          return prev
        }
        const next = new Map(prev)
        next.set(
          state.page,
          new Map(data.listings.map((listing) => [listing.listingId, listing]))
        )
        return next
      }

      let changed = false
      const nextPage = new Map(pinnedPage)
      for (const listing of data.listings) {
        if (
          nextPage.has(listing.listingId) &&
          nextPage.get(listing.listingId) !== listing
        ) {
          nextPage.set(listing.listingId, listing)
          changed = true
        }
      }
      if (!changed) {
        return prev
      }
      const next = new Map(prev)
      next.set(state.page, nextPage)
      return next
    })
  }, [data, state.isSortFrozen, sortFilterKey, isFetching, state.page])

  const visibleData = useMemo(() => {
    const pinnedPage = pinnedPages?.get(state.page)
    if (!data || !pinnedPage) {
      return data
    }
    return {
      ...data,
      listings: Array.from(pinnedPage.values()),
    }
  }, [data, pinnedPages, state.page])

  const [pusherKeyEnt, pusherOptionsEnt] = pusherCredentialsEnt ?? []
  const pusherClientEnt = usePusher(
    hasEntListings ? pusherKeyEnt : null,
    pusherOptionsEnt?.cluster
  )

  const getActiveFilterCount = useCallback(
    () => calculateActiveFilterCount(effectiveState.filters, allFilters),
    [effectiveState.filters, allFilters]
  )

  const dispatchHelpers = useMemo(
    () => ({
      resetFilters: () => dispatch({ type: 'RESET_FILTERS' }),
      setPage: (page: number) =>
        dispatch({
          type: 'SET_PAGE',
          page,
        }),
      setSavedSearch: (id: SavedSearchDto['_id']) => {
        setTimeout(() => {
          setSavedSearchId(id)
        }, 0)
        // Leave pageSize and view unchanged on setSavedSearch
        const { pageSize, view, ...partialState } = effectiveInitialState
        dispatch({
          type: 'SET_STATE',
          partialState,
        })
      },
      setSort: (
        sortBy: AllListingsSortByEnum,
        sortOrder: AllListingsSortOrderEnum
      ) =>
        dispatch({
          type: 'SET_SORT',
          sortBy,
          sortOrder,
        }),
      setPageSize: (pageSize: number) =>
        dispatch({
          type: 'SET_PAGE_SIZE',
          pageSize,
        }),
      setView: (view: StoreFrontOptionsDefaultViewEnum) =>
        dispatch({
          type: 'SET_VIEW',
          view,
        }),
      setSortFrozen: (isSortFrozen: boolean) =>
        dispatch({
          type: 'SET_SORT_FROZEN',
          isSortFrozen,
        }),
      updateFilters: (filters: Partial<ListingFiltersState>) =>
        dispatch({
          type: 'UPDATE_FILTERS',
          filters: maybeFormatFilters(filters, allFilters),
        }),
    }),
    [allFilters, effectiveInitialState, setSavedSearchId]
  )

  const contextValue = useMemo(
    () => ({
      state: effectiveState,
      data: visibleData || null,
      isLoading,
      error,
      dispatch,
      getActiveFilterCount,
      ...dispatchHelpers,
      recordBidPlacedWhileFrozen,
      updateSearchData,
      pusherClient3MP,
      pusherClientEnt,
      currentImpressionId,
      similarSearch: {
        enabled: similarEnabled,
        data: similarData,
        isFetching: similarIsFetching,
        reasonShown: similarReason,
      },
      myBidsFilter,
      personalizationEligibility,
      rankingSurface,
      scope,
      rawCategoryThreshold: state.filters.categoryThreshold,
      isPageSizeRemembered,
    }),
    [
      effectiveState,
      state.filters.categoryThreshold,
      visibleData,
      isLoading,
      error,
      dispatch,
      pusherClient3MP,
      pusherClientEnt,
      dispatchHelpers,
      getActiveFilterCount,
      recordBidPlacedWhileFrozen,
      updateSearchData,
      currentImpressionId,
      similarEnabled,
      similarData,
      similarIsFetching,
      similarReason,
      myBidsFilter,
      personalizationEligibility,
      rankingSurface,
      scope,
      isPageSizeRemembered,
    ]
  )

  const analyticsData = useMemo(
    () => ({
      ...(currentImpressionId && {
        listing_card_impression_id: currentImpressionId,
      }),
    }),
    [currentImpressionId]
  )

  return (
    <ListingSearchContext.Provider value={contextValue}>
      <NestableAnalyticsProvider value={analyticsData}>
        {children}
      </NestableAnalyticsProvider>
    </ListingSearchContext.Provider>
  )
}

export default ListingSearchProvider
